import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Surface, Badge, Button, Grid, PageHeader, Tabs, Alert } from '../../ui/components';
import { ArrowLeft, Info, Upload } from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { BORROWER_NAV } from '../../config/navigation';
import { supabase } from '../../lib/supabase';
import FloatingOwenAssistant from '../../components/common/FloatingOwenAssistant';
import FloatingKnowledge from '../../components/common/FloatingKnowledge';

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--lk-border-subtle)', fontSize: 'var(--lk-text-sm)' }}>
    <span style={{ color: 'var(--lk-text-tertiary)', fontWeight: 500 }}>{label}</span>
    <span style={{ color: 'var(--lk-text-primary)' }}>{value}</span>
  </div>
);

const BorrowerApplication = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [borrower, setBorrower] = useState(null);
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    checkAuth();
  }, [id]);

  const checkAuth = async () => {
    try {
      // Check for demo session first
      const demoSession = localStorage.getItem('lk_demo_session');
      if (demoSession) {
        const demoUser = JSON.parse(demoSession);
        setBorrower({
          id: demoUser.id,
          email: demoUser.email,
          firstName: demoUser.user_metadata?.firstName || 'Demo',
          lastName: demoUser.user_metadata?.lastName || 'Borrower',
        });
        await fetchApplication(id || null, demoUser.id);
        return;
      }

      // Otherwise check Supabase session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        navigate('/borrower/login');
        return;
      }

      setBorrower({
        id: session.user.id,
        email: session.user.email,
        firstName: session.user.user_metadata?.firstName || session.user.email.split('@')[0],
        lastName: session.user.user_metadata?.lastName || '',
      });

      await fetchApplication(id || null, session.user.id);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/borrower/login');
    }
  };

  // Rich demo application for demo users
  const DEMO_APP = {
    id: 'demo-app-001',
    user_id: 'demo-borrower-123',
    anonymous_id: 'LK-7842',
    status: 'in_progress',
    product_type: 'conventional',
    loan_amount: 385000,
    credit_score: 738,
    income_monthly: 9200,
    readiness_score: 62,
    property_info: {
      address: '742 Evergreen Terrace, Springfield',
      type: 'Single Family',
      use: 'Primary Residence',
      purchase_price: 480000,
      down_payment: 95000,
    },
    income_info: {
      monthly: 9200,
      annual: 110400,
      employment_type: 'W-2 Full-Time',
    },
    assets_info: {
      total: 142000,
      liquid: 118000,
    },
    documents_count: 2,
    created_at: new Date(Date.now() - 3 * 86400000).toISOString(),
    updated_at: new Date(Date.now() - 86400000).toISOString(),
  };

  const fetchApplication = async (appId, userId) => {
    setLoading(true);
    try {
      let query = supabase.from('loan_applications').select('*').eq('user_id', userId);

      if (appId) {
        query = query.eq('id', appId).single();
      } else {
        query = query.order('created_at', { ascending: false }).limit(1).single();
      }

      const { data, error } = await query;

      if (error || !data) {
        // Fallback to demo data for demo user
        if (userId === 'demo-borrower-123') {
          setApplication(DEMO_APP);
        } else {
          setApplication(null);
        }
      } else {
        setApplication(data);
      }
    } catch (error) {
      console.error('Error fetching application:', error);
      if (userId === 'demo-borrower-123') {
        setApplication(DEMO_APP);
      } else {
        setApplication(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem('lk_demo_session');
    await supabase.auth.signOut();
    navigate('/');
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toLocaleString()}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!borrower) return null;

  return (
    <AppLayout
      role="borrower"
      navItems={BORROWER_NAV}
      user={{
        name: `${borrower.firstName} ${borrower.lastName}`,
        email: borrower.email,
        role: 'borrower',
      }}
      onLogout={handleLogout}
    >
      <div>
        {loading ? (
          <Surface style={{ textAlign: 'center', padding: 'var(--lk-space-10)' }}>
            <div className="lk-spinner lk-spinner-md" style={{ margin: '0 auto var(--lk-space-3)' }} />
            Loading application...
          </Surface>
        ) : application ? (
          <>
            <PageHeader
              title="Application Details"
              description={application.property_info?.address || 'New Application'}
              actions={
                <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/borrower/dashboard')}>
                  Back to Dashboard
                </Button>
              }
            />

            {/* Status Banner */}
            <Alert variant="info" icon={Info} style={{ marginBottom: 'var(--lk-space-4)' }}>
              <strong>Application Status:</strong> {application.status?.replace(/_/g, ' ')}
              {application.readiness_score && (
                <span style={{ marginLeft: 'var(--lk-space-4)' }}>
                  <strong>Readiness Score:</strong> {application.readiness_score}/100
                </span>
              )}
            </Alert>

            {/* Tabs */}
            <Tabs
              tabs={[
                { id: 0, label: 'Overview' },
                { id: 1, label: 'Property' },
                { id: 2, label: 'Financials' },
                { id: 3, label: 'Documents' },
              ]}
              activeTab={activeTab}
              onChange={setActiveTab}
            />

            <div style={{ marginTop: 'var(--lk-space-4)' }}>
              {/* Tab Content */}
              {activeTab === 0 && (
                <Grid cols={2} gap="var(--lk-space-4)">
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Loan Details</div>
                    <DetailRow label="Product Type" value={
                      <Badge variant="neutral">{application.product_type?.replace(/_/g, ' ') || 'Not Selected'}</Badge>
                    } />
                    <DetailRow label="Loan Amount" value={formatCurrency(application.loan_amount)} />
                    <DetailRow label="Down Payment" value={formatCurrency(application.property_info?.down_payment)} />
                    <DetailRow label="Application Date" value={formatDate(application.created_at)} />
                  </Surface>
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Readiness Score</div>
                    <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-3)' }}>
                      <div style={{ fontSize: 'var(--lk-text-4xl)', fontWeight: 700, lineHeight: 1 }}>{application.readiness_score || 0}</div>
                      <div style={{ color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)' }}>out of 100</div>
                    </div>
                    <div style={{ height: 10, borderRadius: 5, backgroundColor: 'var(--lk-gray-100)' }}>
                      <div style={{
                        height: '100%',
                        width: `${application.readiness_score || 0}%`,
                        borderRadius: 5,
                        backgroundColor: application.readiness_score >= 80 ? 'var(--lk-success-500)' : application.readiness_score >= 60 ? 'var(--lk-warning-500)' : 'var(--lk-danger-500)',
                        transition: 'width 0.6s ease',
                      }} />
                    </div>
                  </Surface>
                </Grid>
              )}

              {activeTab === 1 && (
                <Surface padding="var(--lk-space-5)">
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Property Information</div>
                  <Grid cols={2} gap="var(--lk-space-4)">
                    <div>
                      <DetailRow label="Address" value={application.property_info?.address || '-'} />
                      <DetailRow label="Property Type" value={application.property_info?.type || '-'} />
                      <DetailRow label="Property Use" value={application.property_info?.use || '-'} />
                    </div>
                    <div>
                      <DetailRow label="Purchase Price" value={formatCurrency(application.property_info?.purchase_price)} />
                      <DetailRow label="Down Payment" value={formatCurrency(application.property_info?.down_payment)} />
                      <DetailRow label="Loan Amount" value={formatCurrency(application.loan_amount)} />
                    </div>
                  </Grid>
                </Surface>
              )}

              {activeTab === 2 && (
                <Grid cols={2} gap="var(--lk-space-4)">
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Income</div>
                    <DetailRow label="Monthly Income" value={formatCurrency(application.income_info?.monthly)} />
                    <DetailRow label="Annual Income" value={formatCurrency(application.income_info?.annual)} />
                    <DetailRow label="Employment Type" value={application.income_info?.employment_type || '-'} />
                  </Surface>
                  <Surface padding="var(--lk-space-5)">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Assets</div>
                    <DetailRow label="Total Assets" value={formatCurrency(application.assets_info?.total)} />
                    <DetailRow label="Liquid Assets" value={formatCurrency(application.assets_info?.liquid)} />
                    <DetailRow label="Credit Score" value={application.credit_score || '-'} />
                  </Surface>
                </Grid>
              )}

              {activeTab === 3 && (
                <Surface padding="var(--lk-space-5)">
                  <div style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Required Documents</div>
                  <Alert variant="info" icon={Info} style={{ marginBottom: 'var(--lk-space-4)' }}>
                    Upload your documents in the Documents section to complete your application.
                  </Alert>
                  <Button variant="primary" icon={Upload} onClick={() => navigate('/borrower/documents')}>
                    Go to Documents
                  </Button>
                </Surface>
              )}
            </div>
          </>
        ) : (
          <Surface style={{ textAlign: 'center', padding: 'var(--lk-space-10)' }}>
            <h4 style={{ fontWeight: 600, marginBottom: 'var(--lk-space-3)' }}>Application not found</h4>
            <Button variant="primary" onClick={() => navigate('/borrower/dashboard')}>
              Back to Dashboard
            </Button>
          </Surface>
        )}
      </div>
      <FloatingOwenAssistant context="borrower-application" />
      <FloatingKnowledge context="application" />
    </AppLayout>
  );
};

export default BorrowerApplication;
